import type { ReceivedDataMessage } from '@livekit/components-core'
import { useDataChannel, useLocalParticipant } from '@livekit/components-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { isTeacher } from '../useClassroomSignals'
import { nextBoardRequestDelay, shouldRetryBoardRequest } from './boardSync'
import { ChunkAssembler, chunkScene, SceneTooLargeError } from './chunk'
import { type InkInbound, InkStore, isInkMessage } from './ink'
import {
  type BoardScenePayload,
  INK_COLORS,
  type InkColor,
  type InkStroke,
  type InkTool,
  REQUEST_COOLDOWN_MS,
  SURFACE_TOPIC,
  type SurfaceMessage,
} from './types'

type PublishOptions = { reliable?: boolean; to?: string[] }

/**
 * Lesson surface: whiteboard + annotate-over-share.
 *
 * Teacher mutations are gated on the JWT identity prefix (send + receive),
 * matching celebrations in useClassroomSignals.
 */
export function useSurface(isHost = false) {
  const { localParticipant } = useLocalParticipant()
  const identity = localParticipant?.identity ?? ''
  // `isHost` is the server's exchange role (=== "Host"), known immediately and
  // the single source of truth for who may draw. It intentionally does NOT
  // include Ghost observers, whose token cannot publish data — a drawable board
  // for them would be dead buttons. This is only the LOCAL send affordance; the
  // security boundary is the receive-side isTeacher(from) check below, which
  // stays on the server-minted sender identity and is unaffected.
  const teacher = isHost

  const [boardOpen, setBoardOpen] = useState(false)
  const [boardVersion, setBoardVersion] = useState(0)
  const [boardScene, setBoardScene] = useState<BoardScenePayload | null>(null)
  const [inkArmed, setInkArmed] = useState(false)
  const [inkTool, setInkTool] = useState<InkTool>('pen')
  const [inkColor, setInkColor] = useState<InkColor>(INK_COLORS[0])
  const [inkRevision, setInkRevision] = useState(0)
  // Reactive copy of the share binding. The ref alone was returned to callers,
  // which meant consumers read a value that never triggered a re-render.
  const [shareId, setShareIdState] = useState<string | null>(null)
  /** Non-null when the teacher's last publish was refused, so the UI can say so. */
  const [boardError, setBoardError] = useState<string | null>(null)

  const encoder = useMemo(() => new TextEncoder(), [])
  const decoder = useMemo(() => new TextDecoder(), [])
  const assemblerRef = useRef(new ChunkAssembler())
  const inkStoreRef = useRef(new InkStore())
  const sceneRef = useRef<BoardScenePayload | null>(null)
  const versionRef = useRef(0)
  const boardOpenRef = useRef(false)
  const repliedRef = useRef(false)
  const closedRef = useRef(false)
  const boardSyncedRef = useRef(false)
  const boardRetryAttemptRef = useRef(0)
  const boardRetryTimerRef = useRef<number | null>(null)
  const inkArmedRef = useRef(false)
  const shareIdRef = useRef<string | null>(null)
  const teacherRef = useRef(teacher)
  const identityRef = useRef(identity)
  teacherRef.current = teacher
  identityRef.current = identity
  inkArmedRef.current = inkArmed

  const bumpInk = useCallback(() => setInkRevision((n) => n + 1), [])

  const stopBoardRequestRetry = useCallback(() => {
    if (boardRetryTimerRef.current === null) return
    window.clearTimeout(boardRetryTimerRef.current)
    boardRetryTimerRef.current = null
  }, [])

  /**
   * One state reply per participant per window.
   *
   * A student's client asks for state on join, which is legitimate — but nothing
   * stops a tampered client asking in a loop, and every ask costs the teacher a full
   * chunked scene. The cooldown bounds that to something harmless without
   * refusing the honest late joiner.
   */
  const requestLogRef = useRef(new Map<string, number>())
  const allowRequest = useCallback((from: string) => {
    const now = Date.now()
    const last = requestLogRef.current.get(from) ?? 0
    if (now - last < REQUEST_COOLDOWN_MS) return false
    requestLogRef.current.set(from, now)
    // The map is keyed by identity and a room is capped at 30, so it cannot grow
    // unbounded; prune anything stale so a long lesson stays tidy.
    if (requestLogRef.current.size > 64) {
      for (const [key, at] of requestLogRef.current) {
        if (now - at > REQUEST_COOLDOWN_MS * 4) requestLogRef.current.delete(key)
      }
    }
    return true
  }, [])

  /** Bind ink to the current screen-share track SID, so strokes from a previous
   *  share never bleed onto the next one. */
  const setShareId = useCallback(
    (next: string | null) => {
      if (shareIdRef.current === next) return
      shareIdRef.current = next
      setShareIdState(next)
      inkStoreRef.current.setShareId(next)
      bumpInk()
      if (!next && inkArmedRef.current) {
        setInkArmed(false)
        inkArmedRef.current = false
      }
    },
    [bumpInk],
  )

  const sendRef = useRef<(msg: SurfaceMessage, options?: PublishOptions) => Promise<boolean>>(() =>
    Promise.resolve(false),
  )

  // Stable handler: a fresh inline arrow per render would tear + re-add the
  // room's DataReceived subscription on every Classroom re-render. The body only
  // reads the stable `decoder` and dispatches through `onInboundRef`, so this
  // callback never needs to change identity — keeping the subscription (and
  // therefore board/ink delivery) permanent.
  const onMessage = useCallback(
    (message: ReceivedDataMessage<typeof SURFACE_TOPIC>) => {
      let payload: SurfaceMessage
      try {
        payload = JSON.parse(decoder.decode(message.payload)) as SurfaceMessage
      } catch {
        return
      }
      const from = message.from?.identity ?? 'unknown'
      onInboundRef.current(payload, from)
    },
    [decoder],
  )
  const { send } = useDataChannel(SURFACE_TOPIC, onMessage)

  /**
   * Publish one surface message. **Never rejects** — it resolves `false` instead.
   *
   * That contract is the whole point. The previous version was
   * `void send(...)` inside a synchronous `try/catch`, which cannot catch an
   * async rejection: `send` resolves to `publishData(): Promise<void>`, so the
   * catch only ever covered a synchronous throw from `JSON.stringify` or the
   * encoder, and every REAL failure (oversized packet, publisher disconnected)
   * became an unhandled rejection. The board's `setBoardError` path — written
   * precisely so a teacher learns when the board stops syncing — could therefore
   * never fire for a transport failure, which is the failure that actually
   * happens on a Cairo mobile link.
   *
   * Resolving rather than rejecting is deliberate: ~19 call sites ignore the
   * result, there is no lint rule here to catch a floating promise, and a
   * promise that cannot reject is safe to ignore by construction.
   *
   * `true` means the local publish resolved. It is NOT a delivery receipt — the
   * data channel is still best-effort per peer.
   */
  const publish = useCallback(
    (msg: SurfaceMessage, options?: PublishOptions): Promise<boolean> => {
      let bytes: Uint8Array
      try {
        bytes = encoder.encode(JSON.stringify(msg))
      } catch {
        return Promise.resolve(false)
      }
      return send(bytes, {
        reliable: options?.reliable ?? true,
        destinationIdentities: options?.to,
      }).then(
        () => true,
        () => false,
      )
    },
    [send, encoder],
  )
  sendRef.current = publish

  /**
   * Report a multi-packet scene send that did not fully land.
   *
   * A scene travels as N chunks and the far-side assembler only completes when
   * every one of them arrives, so a single failed packet is a board that has
   * quietly stopped syncing — which is exactly what `boardError` exists to say
   * out loud. Chunks are still fired in PARALLEL rather than awaited in
   * sequence: reassembly is index-based (`i`, `n`), so send order does not
   * matter, and serialising them would only add latency on the link least able
   * to afford it.
   *
   * Verdicts are keyed to a monotonic BUNDLE id, not to the scene version. The
   * scene version cannot do this job: `openBoard` re-sends the current scene
   * WITHOUT incrementing it, so two open-path fan-outs are indistinguishable by
   * version and a slow failure from the first would report over a second that
   * succeeded. Each fan-out also clears the error as it starts, so a successful
   * retry actually retracts the previous verdict instead of leaving a banner up
   * for the rest of the lesson.
   */
  const sendBundleRef = useRef(0)
  const reportChunkFailure = useCallback((sent: Promise<boolean>[]) => {
    const bundle = sendBundleRef.current + 1
    sendBundleRef.current = bundle
    setBoardError(null)
    void Promise.all(sent).then((results) => {
      // Superseded by a later fan-out — that one owns the verdict now.
      if (sendBundleRef.current !== bundle) return
      if (results.every((ok) => ok)) return
      setBoardError('failed')
    })
  }, [])

  const onInboundRef = useRef<(payload: SurfaceMessage, from: string) => void>(() => undefined)

  onInboundRef.current = (payload: SurfaceMessage, from: string) => {
    const me = identityRef.current
    const amTeacher = teacherRef.current

    const teacherOnly =
      payload.kind === 'board_open' ||
      payload.kind === 'board_close' ||
      payload.kind === 'board_clear' ||
      payload.kind === 'board_chunk' ||
      payload.kind === 'ink_arm' ||
      payload.kind === 'ink_clear' ||
      payload.kind === 'ink_undo' ||
      payload.kind === 'ink_begin' ||
      payload.kind === 'ink_point' ||
      payload.kind === 'ink_end' ||
      payload.kind === 'ink_snapshot'

    if (teacherOnly && !isTeacher(from)) return

    switch (payload.kind) {
      case 'board_open':
        repliedRef.current = true
        closedRef.current = false
        boardOpenRef.current = true
        setBoardOpen(true)
        setInkArmed(false)
        inkArmedRef.current = false
        versionRef.current = Math.max(versionRef.current, payload.v)
        setBoardVersion(versionRef.current)
        break
      case 'board_close':
        repliedRef.current = true
        closedRef.current = true
        boardOpenRef.current = false
        setBoardOpen(false)
        assemblerRef.current.clear()
        stopBoardRequestRetry()
        break
      case 'board_clear':
        boardSyncedRef.current = true
        sceneRef.current = { elements: [] }
        setBoardScene({ elements: [] })
        versionRef.current = Math.max(versionRef.current, payload.v)
        setBoardVersion(versionRef.current)
        stopBoardRequestRetry()
        break
      case 'board_chunk': {
        repliedRef.current = true
        closedRef.current = false
        const assembled = assemblerRef.current.push(payload)
        if (!assembled) break
        if (assembled.v < versionRef.current) break
        try {
          const scene = JSON.parse(assembled.json) as BoardScenePayload
          if (!Array.isArray(scene.elements)) break
          sceneRef.current = scene
          versionRef.current = assembled.v
          setBoardVersion(assembled.v)
          setBoardScene(scene)
          boardOpenRef.current = true
          setBoardOpen(true)
          boardSyncedRef.current = true
          stopBoardRequestRetry()
        } catch {
          /* ignore corrupt scene */
        }
        break
      }
      case 'board_request': {
        if (!amTeacher) break
        // Addressed to the ASKER, never broadcast. Replying to the room meant a
        // class of twenty arriving in the first minute produced twenty full-scene
        // storms delivered to all twenty phones — on Cairo mobile that is the
        // difference between a board that appears and a class that stalls.
        const to = [from]
        if (!allowRequest(from)) break
        if (boardOpenRef.current) {
          sendRef.current({ kind: 'board_open', v: versionRef.current, by: me }, { to })
          if (sceneRef.current) {
            try {
              const chunks = chunkScene(sceneRef.current, {
                id: `snap-${Date.now()}`,
                v: versionRef.current,
                by: me,
              })
              for (const chunk of chunks) sendRef.current(chunk, { reliable: true, to })
            } catch {
              /* oversized scene — the asker keeps the empty board rather than
                 the teacher's send loop throwing mid-lesson */
            }
          }
        } else {
          sendRef.current({ kind: 'board_close', v: versionRef.current, by: me }, { to })
        }
        break
      }
      case 'ink_arm':
        // Arming state only. Disarming deliberately leaves the ink on screen:
        // a teacher draws an arrow, disarms so their clicks reach the app they are
        // sharing, and keeps talking over the arrow. Erasing here made that
        // impossible. Wiping is what 'ink_clear' is for.
        setInkArmed(payload.armed)
        inkArmedRef.current = payload.armed
        break
      case 'ink_request': {
        if (!amTeacher) break
        if (!allowRequest(from)) break
        const to = [from]
        const sid = shareIdRef.current
        if (sid) {
          sendRef.current(
            {
              kind: 'ink_snapshot',
              shareId: sid,
              strokes: inkStoreRef.current.snapshot(),
              by: me,
            },
            { to },
          )
        }
        if (inkArmedRef.current) {
          sendRef.current(
            { kind: 'ink_arm', armed: true, shareId: sid ?? undefined, by: me },
            { to },
          )
        }
        break
      }
      default:
        if (isInkMessage(payload)) {
          inkStoreRef.current.applyMessage(payload as InkInbound)
          bumpInk()
        }
        break
    }
  }

  /**
   * Ask on arrival for whatever is already on the board.
   *
   * Guarded by a ref rather than effect deps: `publish` is rebuilt whenever the
   * data channel hands back a new `send`, and without the guard each rebuild
   * re-asked — which on the teacher's side means re-chunking and re-sending the
   * whole scene. The short delay lets the room finish wiring up subscriptions
   * before the request goes out. A request, a chunk, or the teacher's first
   * subscription can be lost, so two cooldown-safe re-asks self-heal that gap.
   */
  const askedRef = useRef(false)
  useEffect(() => {
    if (!identity || askedRef.current) return
    askedRef.current = true
    const armRetry = () => {
      const delay = nextBoardRequestDelay(boardRetryAttemptRef.current)
      if (delay === null) return
      boardRetryTimerRef.current = window.setTimeout(() => {
        boardRetryTimerRef.current = null
        const attempt = boardRetryAttemptRef.current
        if (
          !shouldRetryBoardRequest({
            attempt,
            replied: repliedRef.current,
            boardOpen: boardOpenRef.current,
            assembled: boardSyncedRef.current,
            closed: closedRef.current,
          })
        ) {
          return
        }
        sendRef.current({ kind: 'board_request' })
        sendRef.current({ kind: 'ink_request' })
        boardRetryAttemptRef.current = attempt + 1
        armRetry()
      }, delay)
    }
    const timer = window.setTimeout(() => {
      repliedRef.current = false
      closedRef.current = false
      boardSyncedRef.current = false
      boardRetryAttemptRef.current = 0
      sendRef.current({ kind: 'board_request' })
      sendRef.current({ kind: 'ink_request' })
      armRetry()
    }, 400)
    return () => {
      window.clearTimeout(timer)
      stopBoardRequestRetry()
    }
  }, [identity, stopBoardRequestRetry])

  const openBoard = useCallback(() => {
    if (!teacher) return
    boardOpenRef.current = true
    setBoardOpen(true)
    // Opening is a fresh start: a verdict from an earlier attempt must not greet
    // the teacher on a board they just reopened. The fan-out below re-establishes
    // it if the scene still fails to send.
    setBoardError(null)
    setInkArmed(false)
    inkArmedRef.current = false
    publish({
      kind: 'ink_arm',
      armed: false,
      shareId: shareIdRef.current ?? undefined,
      by: identity,
    })
    const v = versionRef.current
    publish({ kind: 'board_open', v, by: identity })
    if (sceneRef.current) {
      try {
        const chunks = chunkScene(sceneRef.current, {
          id: `open-${Date.now()}`,
          v,
          by: identity,
        })
        reportChunkFailure(chunks.map((chunk) => publish(chunk)))
      } catch {
        setBoardError('tooLarge')
      }
    }
  }, [teacher, identity, publish, reportChunkFailure])

  const closeBoard = useCallback(() => {
    if (!teacher) return
    boardOpenRef.current = false
    setBoardOpen(false)
    publish({ kind: 'board_close', v: versionRef.current, by: identity })
  }, [teacher, identity, publish])

  const clearBoard = useCallback(() => {
    if (!teacher) return
    const v = versionRef.current + 1
    versionRef.current = v
    setBoardVersion(v)
    sceneRef.current = { elements: [] }
    setBoardScene({ elements: [] })
    publish({ kind: 'board_clear', v, by: identity })
  }, [teacher, identity, publish])

  const publishBoardScene = useCallback(
    (scene: BoardScenePayload) => {
      if (!teacher) return
      // Chunk BEFORE committing the version, so a refused scene does not leave
      // this client claiming a version nobody else has.
      let chunks
      try {
        chunks = chunkScene(scene, {
          id: `v${versionRef.current + 1}-${Date.now()}`,
          v: versionRef.current + 1,
          by: identity,
        })
      } catch (error) {
        // Told to the teacher rather than swallowed: a board that has quietly
        // stopped syncing is worse than one that says it is too full.
        setBoardError(error instanceof SceneTooLargeError ? 'tooLarge' : 'failed')
        return
      }
      const v = versionRef.current + 1
      versionRef.current = v
      setBoardVersion(v)
      sceneRef.current = scene
      reportChunkFailure(chunks.map((chunk) => publish(chunk)))
    },
    [teacher, identity, publish, reportChunkFailure],
  )

  const setBoardSceneLocal = useCallback((scene: BoardScenePayload) => {
    sceneRef.current = scene
  }, [])

  const armInk = useCallback(
    (armed: boolean) => {
      if (!teacher) return
      const sid = shareIdRef.current
      if (armed) {
        if (!sid) return // cannot annotate without a live share track
        boardOpenRef.current = false
        setBoardOpen(false)
        publish({ kind: 'board_close', v: versionRef.current, by: identity })
      }
      setInkArmed(armed)
      inkArmedRef.current = armed
      publish({ kind: 'ink_arm', armed, shareId: sid ?? undefined, by: identity })
    },
    [teacher, identity, publish, bumpInk],
  )

  const clearInk = useCallback(() => {
    if (!teacher) return
    const sid = shareIdRef.current
    if (!sid) return
    inkStoreRef.current.clearLocal()
    bumpInk()
    publish({ kind: 'ink_clear', shareId: sid, by: identity })
  }, [teacher, identity, publish, bumpInk])

  const undoInk = useCallback(() => {
    if (!teacher) return
    const sid = shareIdRef.current
    if (!sid) return
    const strokes = inkStoreRef.current.strokes
    const last = strokes[strokes.length - 1]
    if (!last) return
    inkStoreRef.current.applyMessage({
      kind: 'ink_undo',
      id: last.id,
      shareId: sid,
      by: identity,
    })
    bumpInk()
    publish({ kind: 'ink_undo', id: last.id, shareId: sid, by: identity })
  }, [teacher, identity, publish, bumpInk])

  const eraseInk = useCallback(
    (id: string) => {
      if (!teacher) return
      const sid = shareIdRef.current
      if (!sid) return
      // Same shape as undo (remove by id), but the id comes from the eraser's
      // hit-test rather than being the last stroke.
      inkStoreRef.current.applyMessage({ kind: 'ink_undo', id, shareId: sid, by: identity })
      bumpInk()
      publish({ kind: 'ink_undo', id, shareId: sid, by: identity })
    },
    [teacher, identity, publish, bumpInk],
  )

  const publishInk = useCallback(
    (msg: SurfaceMessage, options?: { reliable?: boolean }): Promise<boolean> => {
      if (!teacher) return Promise.resolve(false)
      if (isInkMessage(msg)) {
        inkStoreRef.current.applyMessage(msg)
        bumpInk()
      }
      const reliable = options?.reliable ?? msg.kind !== 'ink_point'
      const sent = publish(msg, { reliable })
      if (msg.kind !== 'ink_end') return sent
      // 'ink_end' is the authoritative copy of the stroke: lose it and the mark
      // never finalizes for anyone else, even though the teacher can still see it.
      // Worth one retry, and the packet itself is idempotent — receivers replace
      // by stroke id, so the same end twice is the same stroke. Only the WIRE is
      // retried; the local store already holds this stroke from the first call.
      //
      // Guarded on the stroke still existing locally, because a retry breaks the
      // ordering that a single reliable channel otherwise guarantees: if the end
      // fails and the teacher undoes, erases or clears in the moment before the
      // retry, an unguarded resend would RESURRECT a stroke they just removed —
      // for the students only, which is the worst possible place to disagree. A
      // share change wipes the store too, so this covers a stale share as well.
      //
      // Deliberately no failure UI. A mid-draw toast would land over the lesson,
      // and points are only ever throttled/lossy — the one thing worth a second
      // attempt is this packet.
      const id = msg.id
      return sent.then((ok) => {
        if (ok) return true
        if (!inkStoreRef.current.hasStroke(id)) return false
        return publish(msg, { reliable: true })
      })
    },
    [teacher, publish, bumpInk],
  )

  const strokes: InkStroke[] = useMemo(() => {
    void inkRevision
    return inkStoreRef.current.allForPaint()
  }, [inkRevision])

  return {
    teacher,
    identity,
    boardOpen,
    boardVersion,
    boardScene,
    openBoard,
    closeBoard,
    clearBoard,
    publishBoardScene,
    setBoardSceneLocal,
    inkArmed,
    inkTool,
    setInkTool,
    inkColor,
    setInkColor,
    armInk,
    clearInk,
    undoInk,
    eraseInk,
    publishInk,
    strokes,
    setShareId,
    shareId,
    boardError,
  }
}

export type SurfaceApi = ReturnType<typeof useSurface>
